import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, Paper, Chip, CircularProgress, Button,
  List, ListItem, ListItemText, ListItemSecondaryAction, Avatar, Divider,
} from '@mui/material';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

import { COLORS } from '../theme/constants';
import { IMAGING_STATUS_CONFIG, PRIORITY_CONFIG, MODALITY_CONFIG, calcImagingTAT } from '../models/imagingModel';

const { primary: IMG_PRIMARY, teal: IMG_TEAL } = COLORS;

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export default function ImagingDashboard() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { activeBranchId, isAllBranches, filterByBranch } = useBranch();
  const [orders, setOrders] = useState([]);
  const [criticalAlerts, setCriticalAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const branchId = activeBranchId || 'default';

    const orderConstraints = [
      where('status', 'not-in', ['reported', 'cancelled']),
      orderBy('status'),
      orderBy('requestedAt', 'desc'),
    ];
    if (!isAllBranches && branchId) orderConstraints.unshift(where('branchId', '==', branchId));

    const unsubOrders = onSnapshot(
      query(collection(db, 'imagingOrders'), ...orderConstraints),
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setOrders(isAllBranches ? filterByBranch(all, { includeUnassigned: true }) : all);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading orders:', err);
        setLoading(false);
      }
    );

    const alertConstraints = [where('isResolved', '==', false), orderBy('createdAt', 'desc')];
    if (!isAllBranches && branchId) alertConstraints.unshift(where('branchId', '==', branchId));

    const unsubAlerts = onSnapshot(
      query(collection(db, 'imagingCriticalAlerts'), ...alertConstraints),
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setCriticalAlerts(isAllBranches ? filterByBranch(all, { includeUnassigned: true }) : all);
      },
      (err) => {
        console.error('Error loading alerts:', err);
      }
    );

    return () => { unsubOrders(); unsubAlerts(); };
  }, [activeBranchId, isAllBranches]);

  const statusCounts = useMemo(() => {
    const counts = { pending_imaging: 0, imaging_done: 0, pending_report: 0, draft_report: 0, approved: 0 };
    orders.forEach((o) => { if (counts[o.status] !== undefined) counts[o.status]++; });
    return counts;
  }, [orders]);

  const statCount = useMemo(() => {
    return orders.filter(o => o.priority === 'stat').length;
  }, [orders]);

  const todayOrders = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return orders
      .filter((o) => {
        const d = o.requestedAt?.toDate ? o.requestedAt.toDate() : new Date(o.requestedAt || 0);
        return d >= today;
      })
      .sort((a, b) => {
        const priorityOrder = { stat: 0, urgent: 1, routine: 2 };
        return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
      });
  }, [orders]);

  const weekChartData = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(now);
      d.setDate(now.getDate() - (6 - i));
      d.setHours(0, 0, 0, 0);
      const next = new Date(d); next.setDate(d.getDate() + 1);
      const dayOrders = orders.filter((o) => {
        const ts = o.requestedAt?.toDate ? o.requestedAt.toDate() : new Date(o.requestedAt || 0);
        return ts >= d && ts < next;
      });
      
      const modalityCounts = { RX: 0, ECO: 0, TC: 0, MRI: 0, DENTAL: 0, ENDOSCOPIA: 0, OTRO: 0 };
      dayOrders.forEach(o => {
        o.studiesRequested?.forEach(s => {
          const mod = s.modality || 'OTRO';
          if (modalityCounts[mod] !== undefined) modalityCounts[mod]++;
        });
      });
      
      return {
        day: DAY_NAMES[d.getDay()],
        ...modalityCounts,
      };
    });
  }, [orders]);

  const averageTAT = useMemo(() => {
    const approvedOrders = orders.filter(o => o.status === 'approved' || o.status === 'reported');
    if (approvedOrders.length === 0) return '—';
    const totalTAT = approvedOrders.reduce((sum, o) => sum + parseFloat(calcImagingTAT(o)), 0);
    return (totalTAT / approvedOrders.length).toFixed(1);
  }, [orders]);

  const overdueCount = useMemo(() => {
    return orders.filter(o => {
      if (o.status === 'reported' || o.status === 'cancelled') return false;
      const tat = parseFloat(calcImagingTAT(o));
      return tat > 24; // 24 horas como TAT estándar
    }).length;
  }, [orders]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      {/* Title bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: IMG_PRIMARY, width: 44, height: 44 }}>
            <BrokenImageIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700} color={IMG_PRIMARY}>Diagnóstico por Imágenes</Typography>
            <Typography variant="caption" color="text.secondary">Dashboard de actividad</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<AddCircleOutlineIcon />}
            onClick={() => navigate('/imaging/new')}
            sx={{ borderColor: IMG_PRIMARY, color: IMG_PRIMARY, fontWeight: 600 }}>
            Nueva Orden
          </Button>
          <Button variant="contained" startIcon={<UploadFileIcon />}
            onClick={() => navigate('/imaging/orders')}
            sx={{ bgcolor: IMG_TEAL, '&:hover': { bgcolor: '#229984' }, fontWeight: 600 }}>
            Ver Órdenes
          </Button>
        </Box>
      </Box>

      {/* Status cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1.5px solid #F59E0B40', bgcolor: '#FEF3C7', cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
            onClick={() => navigate('/imaging/orders?status=pending_imaging')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PendingActionsIcon sx={{ color: '#92400E', fontSize: 28 }} />
              <Box>
                <Typography variant="h4" fontWeight={800} color="#92400E" lineHeight={1}>{statusCounts.pending_imaging}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>Pend. Imágenes</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1.5px solid #3B82F640', bgcolor: '#DBEAFE', cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
            onClick={() => navigate('/imaging/orders?status=imaging_done')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CheckCircleIcon sx={{ color: '#1E40AF', fontSize: 28 }} />
              <Box>
                <Typography variant="h4" fontWeight={800} color="#1E40AF" lineHeight={1}>{statusCounts.imaging_done}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>Imágenes Listas</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1.5px solid #A855F740', bgcolor: '#F3E8FF', cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
            onClick={() => navigate('/imaging/orders?status=pending_report')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PendingActionsIcon sx={{ color: '#6D28D9', fontSize: 28 }} />
              <Box>
                <Typography variant="h4" fontWeight={800} color="#6D28D9" lineHeight={1}>{statusCounts.pending_report}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>Pend. Informe</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1.5px solid #10B98140', bgcolor: '#D1FAE5', cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
            onClick={() => navigate('/imaging/orders?status=approved')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CheckCircleIcon sx={{ color: '#065F46', fontSize: 28 }} />
              <Box>
                <Typography variant="h4" fontWeight={800} color="#065F46" lineHeight={1}>{statusCounts.approved}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>Aprobados</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1.5px solid #DC262640', bgcolor: '#FEE2E2', cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
            onClick={() => navigate('/imaging/orders?priority=stat')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <WarningAmberIcon sx={{ color: '#DC2626', fontSize: 28 }} />
              <Box>
                <Typography variant="h4" fontWeight={800} color="#DC2626" lineHeight={1}>{statCount}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>STAT ⚡</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>TAT Promedio</Typography>
            <Typography variant="h5" fontWeight={700} color={IMG_PRIMARY}>{averageTAT} h</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Estudios Hoy</Typography>
            <Typography variant="h5" fontWeight={700} color={IMG_PRIMARY}>{todayOrders.length}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Hallazgo Crítico</Typography>
            <Typography variant="h5" fontWeight={700} color="#DC2626">{criticalAlerts.length}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Vencidos TAT</Typography>
            <Typography variant="h5" fontWeight={700} color="#DC2626">{overdueCount}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Weekly chart */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="subtitle1" fontWeight={700} color={IMG_PRIMARY} mb={2}>
              Estudios de la semana
            </Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={weekChartData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="RX" fill="#1D4ED8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="ECO" fill="#0F6E56" radius={[4, 4, 0, 0]} />
                <Bar dataKey="TC" fill="#6D28D9" radius={[4, 4, 0, 0]} />
                <Bar dataKey="MRI" fill="#92400E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Today's orders */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0', height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={700} color={IMG_PRIMARY} mb={1}>
              Órdenes de hoy ({todayOrders.length})
            </Typography>
            {todayOrders.length === 0 ? (
              <Typography color="text.secondary" variant="body2" sx={{ mt: 2 }}>Sin órdenes hoy.</Typography>
            ) : (
              <List dense sx={{ maxHeight: 240, overflowY: 'auto' }}>
                {todayOrders.map((o, i) => (
                  <React.Fragment key={o.id}>
                    {i > 0 && <Divider />}
                    <ListItem
                      button
                      onClick={() => navigate(`/imaging/orders/${o.id}`)}
                      sx={{ borderRadius: 1, '&:hover': { bgcolor: '#F0F9FF' } }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {o.priority === 'stat' && (
                              <Chip label="STAT" size="small" sx={{ bgcolor: '#FEE2E2', color: '#DC2626', fontWeight: 700, fontSize: '0.6rem', height: 18 }} />
                            )}
                            {o.priority === 'urgent' && (
                              <Chip label="URGENTE" size="small" sx={{ bgcolor: '#FEF3C7', color: '#D97706', fontWeight: 700, fontSize: '0.6rem', height: 18 }} />
                            )}
                            <Typography variant="body2" fontWeight={600} noWrap>{o.patientName || 'Paciente'}</Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {o.studiesRequested?.slice(0, 2).map((s, idx) => (
                              <Chip key={idx} label={s.modality} size="small" sx={{ fontSize: '0.6rem', bgcolor: MODALITY_CONFIG[s.modality]?.bg, color: MODALITY_CONFIG[s.modality]?.color }} />
                            ))}
                          </Box>
                        }
                        secondaryTypographyProps={{ noWrap: true, fontSize: '0.72rem' }}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={IMAGING_STATUS_CONFIG[o.status]?.label || o.status}
                          size="small"
                          sx={{ fontSize: '0.65rem', bgcolor: IMAGING_STATUS_CONFIG[o.status]?.bg, color: IMAGING_STATUS_CONFIG[o.status]?.color, fontWeight: 600 }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Critical alerts */}
        {criticalAlerts.length > 0 && (
          <Grid size={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1.5px solid #FECACA', bgcolor: '#FFF5F5' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <WarningAmberIcon sx={{ color: '#DC2626' }} />
                <Typography variant="subtitle1" fontWeight={700} color="#DC2626">
                  Alertas — Hallazgos Críticos ({criticalAlerts.length})
                </Typography>
              </Box>
              {criticalAlerts.map((alert) => (
                <Box key={alert.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, mb: 1, bgcolor: '#FEE2E2', borderRadius: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={700} color="#991B1B">{alert.patientName}</Typography>
                    <Typography variant="caption" color="#DC2626">
                      {alert.criticalFindings?.map(f => f.finding).join(' | ')}
                    </Typography>
                  </Box>
                  <Button size="small" variant="outlined" sx={{ borderColor: '#DC2626', color: '#DC2626', fontSize: '0.7rem' }}
                    onClick={() => navigate(`/imaging/orders/${alert.orderId}`)}>
                    Ver Orden
                  </Button>
                </Box>
              ))}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
